import React from "react";
import { Shield, Business, AccountBalance } from "@mui/icons-material";

const PolicyTab = ({ loan }) => {
  const policyDetails = loan?.meta?.insuranceDetails || {};

  // Check if it's a personal loan (don't show policy)
  const isPersonalLoan = loan?.meta?.isPersonalLoan ||
    (loan?.loanProduct?.categoryName || "").toLowerCase().includes("personal");

  if (isPersonalLoan) {
    return (
      <div className="p-12 text-center">
        <Shield className="mx-auto text-gray-400 mb-4" style={{ fontSize: 64 }} />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">No Policy Information</h3>
        <p className="text-gray-500">
          Policy information is not applicable for personal loans.
        </p>
      </div>
    );
  }

  if (!policyDetails || Object.keys(policyDetails).length === 0) {
    return (
      <div className="p-12 text-center">
        <Shield className="mx-auto text-gray-400 mb-4" style={{ fontSize: 64 }} />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">No Policy Information</h3>
        <p className="text-gray-500">
          No insurance policy details available for this loan.
        </p>
      </div>
    );
  }

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "$0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Policy Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Shield className="text-blue-600" />
          Policy Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Policy Number</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.policy_number || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Policy Type</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.policy_type || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">License Plate No.</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.plate_number || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Total Premium</p>
            <p className="text-base font-semibold text-gray-900">
              {formatCurrency(policyDetails.insurance_total_premium)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Premium Financed</p>
            <p className="text-base font-semibold text-gray-900">
              {formatCurrency(policyDetails.total_policy_amount)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Downpayment</p>
            <p className="text-base font-semibold text-gray-900">
              {formatCurrency(policyDetails.down_payment)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Period of Insurance</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.period_of_insurance || "N/A"}
            </p>
          </div>
        </div>
      </div>

      {/* Insurer Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Business className="text-blue-600" />
          Insurer Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Insurer Name</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.company_name || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Address</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.company_address || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Phone Number</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.company_phone_number || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Broker Name</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.broker_name || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Contact Person</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.contact_person || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Contact Email</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.contact_email || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Company Email</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.company_email || "N/A"}
            </p>
          </div>
        </div>
      </div>

      {/* Payment Information */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <AccountBalance className="text-blue-600" />
          Payment Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600 mb-1">Bank Name</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.name_of_bank || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Account Number</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.bank_account_number || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Account Type</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.bank_account_type || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Pay Date</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.pay_date || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-1">Payment Type</p>
            <p className="text-base font-semibold text-gray-900">
              {policyDetails.payment_type || "N/A"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyTab;
